import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

import type { PageActionsContextValue } from "@/types/page-actions.types";

const PageActionsContext = createContext<PageActionsContextValue | undefined>(undefined);

export function PageActionsProvider({ children }: { children: ReactNode }) {
    const [slot, setSlot] = useState<HTMLElement | null>(null);
    const [actionCount, setActionCount] = useState(0);
    const [timeSlot, setTimeSlot] = useState<HTMLElement | null>(null);
    const [timeCount, setTimeCount] = useState(0);

    const register = useCallback(() => {
        setActionCount((count) => count + 1);
        return () => {
            setActionCount((count) => Math.max(0, count - 1));
        };
    }, []);

    const registerTime = useCallback(() => {
        setTimeCount((count) => count + 1);
        return () => {
            setTimeCount((count) => Math.max(0, count - 1));
        };
    }, []);

    const value = useMemo(
        () => ({
            slot,
            setSlot,
            actionCount,
            register,
            timeSlot,
            setTimeSlot,
            timeCount,
            registerTime,
        }),
        [actionCount, register, registerTime, slot, timeCount, timeSlot]
    );

    return <PageActionsContext.Provider value={value}>{children}</PageActionsContext.Provider>;
}

export function usePageActions() {
    const context = useContext(PageActionsContext);
    if (context === undefined) {
        throw new Error("usePageActions must be used within a PageActionsProvider");
    }
    return context;
}
